import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import type { PrivacyConfig } from "../types";
import { colorSchemes } from "../constants";
import { Card } from "@/components/ui/card";

interface CookieConsentProps {
  privacyConfig: PrivacyConfig;
  colors: typeof colorSchemes.default;
}

export const CookieConsent = ({ privacyConfig, colors }: CookieConsentProps) => {
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Check cookie consent status whenever component mounts or updates
    const checkCookieConsent = () => {
      const hasConsent = localStorage.getItem("cookieConsent");
      setIsVisible(!hasConsent);
    };

    // Add event listener for storage changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'cookieConsent' && e.newValue === null) {
        setIsVisible(true);
      }
    };

    // Check initial state
    checkCookieConsent();

    // Listen for storage events from other tabs
    window.addEventListener('storage', handleStorageChange);

    // Create a custom event listener for same-tab updates
    const handleCustomReset = () => {
      setIsVisible(true);
    };
    window.addEventListener('cookieConsentReset', handleCustomReset);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cookieConsentReset', handleCustomReset);
    };
  }, []);

  const handleAccept = () => {
    setIsVisible(false);
    localStorage.setItem("cookieConsent", "true");
    toast.success("Cookie preferences saved");
  };

  if (!privacyConfig.enabled || !isVisible) {
    return null;
  }

  return (
    <>
      <Card 
        className="absolute bottom-4 left-4 z-50 w-full max-w-[calc(100%-2rem)] sm:max-w-sm md:max-w-md animate-fade-in transition-opacity duration-300"
      >
        <div 
          className="p-4 rounded-lg shadow-lg"
          style={{ 
            background: `${colors.light}`,
            border: `1px solid ${colors.primary}20`
          }}
        >
          <p 
            className="text-sm mb-4" 
            style={{ color: colors.dark }}
          >
            {privacyConfig.cookieConsentText}
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPrivacyPolicy(true)}
              className="flex-1 sm:flex-none order-2 sm:order-1"
              style={{ 
                borderColor: colors.primary,
                color: colors.primary
              }}
            >
              {privacyConfig.learnMoreButtonText}
            </Button>
            <Button
              onClick={handleAccept}
              className="flex-1 sm:flex-none order-1 sm:order-2"
              style={{ 
                background: colors.primary,
                color: colors.light
              }}
            >
              {privacyConfig.acceptButtonText}
            </Button>
          </div>
        </div>
      </Card>

      <Dialog open={showPrivacyPolicy} onOpenChange={setShowPrivacyPolicy}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{privacyConfig.title}</DialogTitle>
          </DialogHeader>
          <div className="mt-4 prose max-w-none">
            <div dangerouslySetInnerHTML={{ __html: privacyConfig.content }} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};